import {connect} from 'react-redux';

import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {CollapsedItemKeyPrefixesEnum} from '../../../../models/api/localPreferencesModel';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {VisualWorkspaceTeamData} from '../../../../models/api/visualWorkspaceModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
  WorkspaceDisplayChevronActionsEnum,
  WorkspaceDisplayChevronSectionsEnum,
} from '../../../../services/tracking';
import {AppState} from '../../../../store/appState';
import {getHasPermission} from '../../../../store/data/access/accessSelectors';
import {toggleExpandItemAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {getFindIsExpanded} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {getUserLicenseType} from '../../../../store/data/teammates/teammatesSelectors';
import {InboxesPanelId} from '../../../../store/panels/panelStateBase';
import {TeamWorkspace} from './teamWorkspace';

/*
 * Constants.
 */

const defaultIsTeamWorkspaceExpanded = true;

/*
 * Props.
 */

export interface TeamWorkspaceContainerProps {
  panelId: InboxesPanelId;
  /** The team that this workspace is for. */
  team: TeamModel;
  /** Teams data passed from VisualWorkspaceModel */
  visualWorkspaceTeam: VisualWorkspaceTeamData;
}

/*
 * Selectors.
 */

/** Extract the specified team from the props. */
function getTeam(_state: AppState, props: TeamWorkspaceContainerProps) {
  return props.team;
}

function buildTeamWorkspaceExpandedKey(teamNamespace: string) {
  return `${CollapsedItemKeyPrefixesEnum.WORKSPACE}${teamNamespace}`;
}

// Find the expanded state for the specified team. */
function makeGetIsExpanded() {
  return createSelector([getTeam, getFindIsExpanded], (team, findIsExpanded) =>
    findIsExpanded(buildTeamWorkspaceExpandedKey(team.namespace), defaultIsTeamWorkspaceExpanded),
  );
}

function makeGetHasAddInboxPermission() {
  return createSelector([getTeam, getHasPermission], (team, hasPermission) =>
    hasPermission(team.namespace, PermissionsEnum.CREATE_INBOX),
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<TeamWorkspaceContainerProps>()(() => ({
  userLicenseType: getUserLicenseType,
  hasAddInboxPermission: makeGetHasAddInboxPermission(),
  isExpanded: makeGetIsExpanded(),
}));

export const TeamWorkspaceContainer = connect(
  mapStateToProps,
  {toggleExpandItemAction},
  (stateProps, dispatchProps, ownProps) => {
    // Build the method to toggle the expanded state.
    const onToggleExpandClick = () => {
      trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_CHEVRON_CLICK, {
        location: WorkspaceDisplayChevronSectionsEnum.TEAM_SECTION,
        action: stateProps.isExpanded
          ? WorkspaceDisplayChevronActionsEnum.COLLAPSE
          : WorkspaceDisplayChevronActionsEnum.EXPAND,
      });
      dispatchProps.toggleExpandItemAction(
        buildTeamWorkspaceExpandedKey(ownProps.team.namespace),
        defaultIsTeamWorkspaceExpanded,
      );
    };

    // Merge the props.
    return {
      ...stateProps,
      ...ownProps,
      onToggleExpandClick,
    };
  },
)(TeamWorkspace);
